import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../css/Leaderboard.css';
import useLeaderboard from '../hooks/useLeaderboard';
import useSeason from '../hooks/useSeason';
import useEventNames from '../hooks/useEventNames';
import useLeague from '../hooks/useLeague';
import { useFriends } from '../hooks/useFriends';
import FantasyTeamView from './FantasyTeamView';

const Leaderboard = ({ event, league, fullSize = false }) => {
    const { leaderboard, loading, error, fetchLeaderboardForEvent, fetchLeaderboardForSeason, fetchLeaderboardForLeague, fetchPremiumLeaderboardForSeason, getUserId, clearLeaderboard } = useLeaderboard();
    const { seasons, fetchSeasons, currentSeason, fetchCurrentSeason, seasonRewards, fetchSeasonRewards } = useSeason();
    const { getFriends } = useFriends();
    const navigate = useNavigate();
    const [season, setSeason] = useState(null);
    const [view, setView] = useState('global'); // State to manage the view type
    const [eventDropdown, setEventDropdown] = useState(event ? event._id : null);
    const [eventRewards, setEventRewards] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null); // State to store the current user ID
    const [friends, setFriends] = useState([]); // State to store current user's friends
    const [currentPage, setCurrentPage] = useState(1);
    const entriesPerPage = 10;
    const [selectedUserID, setSelectedUserID] = useState(null); // State for selected user ID
    const [showOverlay, setShowOverlay] = useState(false); // State for overlay visibility
    const [selectedEvent, setSelectedEvent] = useState(null); // State for selected team
    const { fetchEventNames, eventNames, loading: loadingEventNames, error: errorEventNames } = useEventNames();
    const { getLeagueRewards, fetchCurrentUserLeague } = useLeague();
    const [currentLeague, setCurrentLeague] = useState(null);
    const [leagueSeasonalRewards, setSeasonalRewardsState] = useState();
    const [leagueEventRewards, setEventRewardsState] = useState();
    const [isLoadingLeague, setIsLoadingLeague] = useState(false);

    // Determine leaderboard type
    const getLeaderboardType = () => {
        if (event) return 'event';
        if (league || currentLeague) return 'league';
        if (view === 'league') return 'user-league';
        if (view === 'friends') return 'friends';
        if (view === 'premium') return 'premium';
        return 'season';
    };

    // Initialize current league based on props or user selection
    useEffect(() => {
        if (league) {
            setCurrentLeague(league);
        } else if (view === 'league' && !currentLeague && !isLoadingLeague) {
            setIsLoadingLeague(true);
            const loadUserLeague = async () => {
                try {
                    const result = await fetchCurrentUserLeague();
                    if (result?.success && result?.league) {
                        setCurrentLeague(result.league);
                        console.log('Current league loaded:', result.league);
                    } else {
                        setCurrentLeague(null);
                    }
                } catch (error) {
                    console.error('Error fetching user league:', error);
                    setCurrentLeague(null);
                } finally {
                    setIsLoadingLeague(false);
                }
            };
            loadUserLeague();
        } else if (view !== 'league' && !league) {
            setCurrentLeague(null);
            setSeasonalRewardsState([]);
            setEventRewardsState([]);
        }
    }, [league, view]);

    // Main leaderboard data fetching effect
    useEffect(() => {
        const leaderboardType = getLeaderboardType();

        const fetchData = async () => {
            // For all non-event, non-league types, we always need season leaderboard data
            // because it contains the teams structure needed for event filtering
            const needsSeasonData = leaderboardType === 'season';

            switch (leaderboardType) {
                case 'event':
                    if (event) {
                        // Pass league ID if in league view
                        const leagueIdForEvent = view === 'league' && currentLeague ? currentLeague._id : null;
                        fetchLeaderboardForEvent(event, leagueIdForEvent);
                        setEventRewards(event.rewards);
                        // Fetch league event rewards if in league view
                        if (view === 'league' && currentLeague) {
                            try {
                                if (!currentSeason) {
                                    fetchCurrentSeason();
                                } else if (currentSeason && currentLeague) {
                                    console.log('Fetching league event rewards...');
                                    const result = await getLeagueRewards(currentLeague._id, currentSeason._id);
                                    if (result.success) {
                                        setEventRewardsState(result.eventRewards || []);
                                    }
                                }
                            } catch (error) {
                                console.error('Error loading league event rewards:', error);
                            }
                        }
                    }
                    break;

                case 'league':
                case 'user-league':
                    if (currentLeague && season) {
                        fetchLeaderboardForLeague(currentLeague._id, season._id);
                        await fetchEventNames(season._id);

                        // Load league rewards
                        try {
                            const result = await getLeagueRewards(currentLeague._id, season._id);
                            if (result.success) {
                                setSeasonalRewardsState(result.seasonalRewards || []);
                                setEventRewardsState(result.eventRewards || []);
                            }
                        } catch (error) {
                            console.error('Error loading league rewards:', error);
                        }
                    } else if (currentLeague && !season) {
                        // Initialize seasons if we have a league but no season
                        if (!seasons || seasons.length === 0) {
                            fetchSeasons();
                        }
                        if (!currentSeason) {
                            fetchCurrentSeason();
                        }
                    }
                    break;

                case 'premium':
                    // Fetch premium leaderboard for season
                    if (season) {
                        fetchPremiumLeaderboardForSeason(season._id);
                        fetchEventNames(season._id);
                    } else {
                        if (!seasons || seasons.length === 0) {
                            fetchSeasons();
                        }
                        if (!currentSeason) {
                            fetchCurrentSeason();
                        }
                        // Fetch premium leaderboard for current season
                        fetchPremiumLeaderboardForSeason();
                    }
                    break;

                case 'season':
                default:
                    // Always fetch season leaderboard for season type (includes global and friends views)
                    // This ensures we have the teams structure needed for event filtering
                    if (season) {
                        fetchLeaderboardForSeason(season._id);
                        fetchEventNames(season._id);
                    } else {
                        if (!seasons || seasons.length === 0) {
                            fetchSeasons();
                        }
                        if (!currentSeason) {
                            fetchCurrentSeason();
                        }
                        // Always fetch season leaderboard to get teams structure for event filtering
                        fetchLeaderboardForSeason();
                    }
                    break;
            }
        };

        // Don't fetch if we're currently loading a league
        if (!isLoadingLeague) {
            fetchData();
        }
        // eslint-disable-next-line
    }, [event, season, currentLeague, view]);

    useEffect(() => {
        if (eventDropdown && eventDropdown._id) {
            setEventRewards(eventDropdown.rewards);
        }
    }, [eventDropdown]);

    useEffect(() => {
        setSeason(currentSeason);
    }, [currentSeason]);

    // Fetch season rewards when premium view is selected and season changes
    useEffect(() => {
        if (view === 'premium' && season && season._id) {
            fetchSeasonRewards(season._id);
        }
    }, [view, season]);

    // Fetch the current user ID when the component mounts
    useEffect(() => {
        const fetchCurrentUserId = async () => {
            const userId = await getUserId();
            setCurrentUserId(userId);
        };

        fetchCurrentUserId();
        fetchCurrentSeason();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch the current user's friends when currentUserId is available
    useEffect(() => {
        const fetchUserFriends = async () => {
            if (currentUserId) {
                try {
                    const friendsList = await getFriends();
                    setFriends(friendsList || []);
                } catch (error) {
                    console.error('Error fetching friends');
                    setFriends([]);
                }
            }
        };

        fetchUserFriends();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserId]);

    // Check if a user is a friend
    const isFriend = (userId) => {
        if (!friends || friends.length === 0) return false;

        const friendMatch = friends.some(friend => {
            // Handle different possible data structures
            const friendId = friend.friend?._id || friend.friend || friend._id;
            const isMatch = friendId === userId;

            return isMatch;
        });

        return friendMatch;
    };

    // Build processedLeaderboard as before
    let processedLeaderboard = leaderboard;
    if (eventDropdown && !event && leaderboard && leaderboard.length > 0) {
        // Check if we have the teams structure needed for event filtering
        if (leaderboard[0].teams) {
            processedLeaderboard = leaderboard
                .map(user => {
                    // Find the fantasy team for the selected event
                    const team = user.teams.find(t => t.event && (t.event._id === eventDropdown._id || t.event._id === eventDropdown || t.event.hltv_id === eventDropdown._id || t.event.hltv_id === eventDropdown));
                    if (!team) return null;
                    // Structure to maintain compatibility with friends filtering
                    return {
                        _id: user._id,  // Keep original user ID at root level
                        username: user.username,
                        userNumber: user.userNumber,
                        points: team.points,  // Use team points for the event
                        boostPoints: team.boostPoints,
                        event: team.event,
                        // Preserve league membership status
                        isCurrentMember: user.isCurrentMember,
                        isBanned: user.isBanned,
                        isPremium: user.isPremium,
                        // Keep original user structure for compatibility
                        user: {
                            _id: user._id,
                            username: user.username,
                            userNumber: user.userNumber,
                        }
                    };
                })
                .filter(Boolean)
                .sort((a, b) => b.points - a.points);
        } else {
            // If we don't have teams structure but an event is selected, 
            // we might need to refetch with the proper data structure
            console.log('Event selected but leaderboard data does not have teams structure - need to refetch season data');
        }
    }

    // Filter for friends view (include current user as well)
    let filteredLeaderboard = processedLeaderboard;
    if (view === 'friends' && processedLeaderboard) {
        filteredLeaderboard = processedLeaderboard.filter(user => {
            const id = user._id || (user.user && user.user._id);
            const isUserFriend = isFriend(id);
            const isCurrentUser = id === currentUserId;
            const shouldInclude = isUserFriend || isCurrentUser;

            return shouldInclude;
        });
    }
    // For global, show all
    // (other views can be added similarly)

    // Helper functions for determining what to show
    const isEventLeaderboard = () => {
        return getLeaderboardType() === 'event' || eventDropdown;
    };

    const isLeagueLeaderboard = () => {
        return ['league', 'user-league'].includes(getLeaderboardType());
    };

    const shouldShowRewards = () => {
        return isEventLeaderboard() || (isLeagueLeaderboard() && !eventDropdown || getLeaderboardType() === 'premium');
    };

    const shouldShowBoostPoints = () => {
        return isEventLeaderboard();
    };

    const shouldShowEventsWon = () => {
        return !isEventLeaderboard() && !isLeagueLeaderboard() && !eventDropdown && !(getLeaderboardType() === 'premium');
    };

    const shouldShowEventsParticipated = () => {
        return !isEventLeaderboard() && !eventDropdown;
    };

    // Calculate the current entries to display
    const indexOfLastEntry = currentPage * entriesPerPage;
    const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
    const currentEntries = filteredLeaderboard ? filteredLeaderboard.slice(indexOfFirstEntry, indexOfFirstEntry + entriesPerPage) : [];

    // Calculate active user positions (excluding banned/left users from position counting)
    const activeUserPositions = {};
    if (isLeagueLeaderboard() && filteredLeaderboard) {
        let activePosition = 1;
        filteredLeaderboard.forEach((item) => {
            const isCurrentMember = item.isCurrentMember;
            const isBanned = item.isBanned;
            const isInactive = isCurrentMember === false || isBanned;

            if (!isInactive) {
                activeUserPositions[item._id] = activePosition;
                activePosition++;
            }
        });
    }

    // Add placeholders to always have 10 entries
    const placeholders = Array.from({ length: entriesPerPage - currentEntries.length });

    // Calculate total pages
    const totalPages = filteredLeaderboard ? Math.ceil(filteredLeaderboard.length / entriesPerPage) : 1;

    const openFantasyTeamView = (userID) => {
        setSelectedUserID(userID); // Set the selected user ID to fetch the fantasy team
        setShowOverlay(true); // Show the overlay
    };

    const getUsername = (userId) => {
        const user = leaderboard.find(item => item._id === userId);
        return user ? user.username : '';
    };

    const closeOverlay = () => {
        setShowOverlay(false); // Hide the overlay
        setSelectedEvent(null); // Reset the selected event
    };

    const closeSelectedEvent = () => {
        setSelectedEvent(null);
    }

    const changeView = (newView) => {
        // Reset relevant state when changing views
        if (view !== newView) {
            // Clear leaderboard data immediately and cancel any pending requests
            clearLeaderboard();
            
            setView(newView);
            setCurrentPage(1); // Reset to first page when changing view

            // Clear league-specific state when leaving league view
            if (view === 'league' && newView !== 'league') {
                setCurrentLeague(null);
                setSeasonalRewardsState([]);
                setEventRewardsState([]);
            }
        }
    };

    // State for current user index
    const [currentUserIndex, setCurrentUserIndex] = useState(0);

    // Update currentUserIndex whenever filteredLeaderboard, eventDropdown, season, or event changes
    useEffect(() => {
        if (filteredLeaderboard && currentUserId) {
            const idx = filteredLeaderboard.findIndex(item => item._id === currentUserId || (item.user && item.user._id === currentUserId));
            setCurrentUserIndex(idx >= 0 ? idx + 1 : 0);
        } else {
            setCurrentUserIndex(0);
        }
    }, [filteredLeaderboard, currentUserId, eventDropdown, season, event]);

    const goToPlacement = () => {
        if (currentUserIndex !== 0) {
            const pageNumber = Math.ceil((currentUserIndex + 1) / entriesPerPage);
            setCurrentPage(pageNumber);
        }
    }

    const getPointsStyle = (point) => {
        return {
            color: point < 0 ? 'var(--lightred)' : point > 0 ? 'var(--lightgreen)' : 'inherit'
        };
    };

    // Function to determine reward text size based on length
    const getRewardStyle = (text) => {
        if (!text) return {};
        const length = text.length;
        let fontSize = '0.83vw';

        if (length > 30) {
            fontSize = '0.5vw';
        } else if (length > 25) {
            fontSize = '0.6vw';
        } else if (length > 20) {
            fontSize = '0.7vw';
        }

        return {
            fontSize: fontSize,
        };
    };

    // Get the appropriate reward text for a position
    const getRewardText = (userId, isInactive) => {
        if (isInactive) return '';

        // Use active position instead of array index
        let position = isLeagueLeaderboard() ? activeUserPositions[userId] :
            (filteredLeaderboard ? filteredLeaderboard.findIndex(item => item._id === userId) + 1 : 0);

        // If event is present and view is friends, use global position for rewards
        if (event && view === 'friends' && leaderboard) {
            const globalPosition = leaderboard.findIndex(item => item._id === userId) + 1;
            position = globalPosition;
        }
        // If event is present and view is league, use leagueEventRewards
        if (event && view === 'league' && leagueEventRewards?.length > 0) {
            // Find the event reward for the current event
            const eventReward = leagueEventRewards.find(er => er.event._id === event._id || er.event._id === event);
            return eventReward?.rewards.find(reward => reward.position === position)?.reward || '-';
        }

        if (isEventLeaderboard() && eventDropdown) {
            // Event-specific rewards in league context
            if (isLeagueLeaderboard()) {
                let usedPosition = position;
                // If view is friends, use global position for rewards
                if (view === 'friends' && leaderboard) {
                    const globalPosition = leaderboard.findIndex(item => item._id === userId) + 1;
                    usedPosition = globalPosition;
                }
                const eventReward = leagueEventRewards.find(er => er.event._id === eventDropdown._id);
                return eventReward?.rewards.find(reward => reward.position === usedPosition)?.reward || '-';
            }
            // Standard event rewards
            let usedPosition = position;
            if (view === 'friends' && leaderboard) {
                const globalPosition = leaderboard.findIndex(item => item._id === userId) + 1;
                usedPosition = globalPosition;
            }
            const rewardIndex = usedPosition - 1;
            return eventRewards ? `${eventRewards[rewardIndex]?.coins || 0} Coins` : '0 Coins';
        }

        if (isLeagueLeaderboard() && !eventDropdown) {
            // League seasonal rewards
            return leagueSeasonalRewards?.length > 0
                ? (leagueSeasonalRewards.find(reward => reward.position === position)?.reward || '-')
                : '-';
        }

        // Premium season rewards
        if (getLeaderboardType() === 'premium' && seasonRewards) {
            const premiumReward = seasonRewards.rewards?.premium?.find(reward => reward.position === position);
            return premiumReward?.reward || '-';
        }

        return '';
    };

    return (
        <div className={`main-container ${fullSize ? '' : 'col-10 offset-1'}`}>
            <div className='title-container'>
                <h1 className='title'>Leaderboard</h1>
                <div className="view-button-container">
                    {!league &&
                        <>
                            <button className={`view-button ${view === 'global' ? 'active' : ''}`} onClick={() => changeView('global')}>
                                Global
                            </button>
                            <button className={`view-button ${view === 'friends' ? 'active' : ''}`} onClick={() => changeView('friends')}>
                                Friends
                            </button>
                            <button className={`view-button ${view === 'league' ? 'active' : ''}`} onClick={() => changeView('league')}>
                                League
                            </button>
                            <button className={` view-button ${view === 'premium' ? 'active' : ''}`} onClick={() => changeView('premium')}>
                                Premium
                            </button>
                        </>
                    }

                    {currentUserIndex > 0 &&
                        <button className='view-button' onClick={goToPlacement}>
                            Current Placement: {currentUserIndex}
                        </button>}
                </div>
                {!event && <div className='inventory-box-filter'>
                    <div className="dropdown">
                        <button id="dropdown-button" style={{ width: '17vw' }} className="dropdown-button">{eventDropdown ? eventDropdown.name : 'Select Event'}</button>
                        <div id="dropdown-content" className="dropdown-content">
                            {loadingEventNames && <div className='loader-small' />}
                            {!loadingEventNames && errorEventNames && <p class>Error loading events</p>}
                            {eventNames && eventNames.length > 0 && !loadingEventNames && <p onClick={() => setEventDropdown(null)}> All Events</p>}
                            {eventNames && eventNames.length > 0 && !loadingEventNames && (
                                eventNames.map((event, index) => (
                                    <p key={index} onClick={() => setEventDropdown(event)}>{event.name}</p>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="dropdown">
                        <button id="dropdown-button" className="dropdown-button">{season ? season.name : 'Select Season'}</button>
                        <div id="dropdown-content" className="dropdown-content">
                            {seasons ? (
                                seasons.map((season, index) => (
                                    <p key={index} onClick={() => setSeason(season)}>{season.name}</p>
                                ))
                            ) : (
                                <div className='loader' />
                            )}
                        </div>
                    </div>
                </div>}
            </div>
            <div className='leaderbord-container'>
                <div className='leaderboard-desc-container'>
                    <h4 className='col-1' style={{ fontFamily: "inherit" }}>#</h4>
                    <h4 className='col-2'>User</h4>
                    <h4 className='col-2'>Fantasy Points</h4>
                    {shouldShowBoostPoints() && <h4 className='col-2'>Boost Points</h4>}
                    {shouldShowEventsParticipated() && <h4 className='col-2'>Events Participated</h4>}
                    {shouldShowEventsWon() && <h4 className='col-2'>Events Won</h4>}
                    {shouldShowRewards() && <h4 className='col-3'>Rewards</h4>}
                </div>
                <div className='leaderboard-content'>
                    {view === 'league' && !currentLeague && !league && !isLoadingLeague ? (
                        <div className='no-packs-container'>
                            <p>You are not part of any league currently</p>
                        </div>
                    ) : filteredLeaderboard && !loading && !isLoadingLeague ? (
                        <>
                            {currentEntries.map((item, index) => {
                                // Support both structures: item._id or item.user._id
                                let userId, userNumber, username, isCurrentMember, isBanned, isPremium;
                                if (item.user && item.user._id) {
                                    userId = item.user._id;
                                    userNumber = item.user.userNumber;
                                    username = item.user.username;
                                    isCurrentMember = item.isCurrentMember;
                                    isBanned = item.isBanned;
                                    isPremium = item?.user?.isPremium;
                                } else {
                                    userId = item._id;
                                    userNumber = item.userNumber;
                                    username = item.username;
                                    isCurrentMember = item.isCurrentMember;
                                    isBanned = item.isBanned;
                                    isPremium = item?.isPremium;
                                }

                                // Determine if user is inactive (left or banned)
                                const isInactive = isLeagueLeaderboard() && (isCurrentMember === false || isBanned);

                                // Get position - use active position for league, or regular position for other views
                                const position = isInactive ? null :
                                    (isLeagueLeaderboard() ? activeUserPositions[userId] : indexOfFirstEntry + index + 1);

                                // Determine display username based on league status
                                let displayUsername = username;
                                if (isLeagueLeaderboard()) {
                                    if (isBanned) {
                                        displayUsername = "(Banned from league)";
                                    } else if (isCurrentMember === false) {
                                        displayUsername = `${username} (Left)`;
                                    }
                                }

                                const rewardText = getRewardText(userId, isInactive);

                                return (
                                    <div
                                        className={`leaderboard-entry-container ${userId === currentUserId ? 'highlight' : ''} ${isFriend(userId) ? 'friend-highlight' : ''} ${isInactive ? 'left' : ''} ${item.isPremium ? 'premium-user' : ''}`}
                                        key={index} onClick={() => fullSize ? (openFantasyTeamView(userId)) : (eventDropdown ? (setSelectedUserID(userId), setSelectedEvent(item.event)) : openFantasyTeamView(userId))}
                                    >
                                        <p className='col-1'> {isInactive ? '-' : `#${position}`}</p>
                                        <div className='col-2' style={{ height: '100%', display: 'flex', justifyContent: 'center' }}>
                                            <p className='username-clickable' onClick={(e) => {
                                                e.stopPropagation();
                                                if (userNumber && !isBanned) {
                                                    navigate(`/profile/${userNumber}`);
                                                } else {
                                                    console.error('userNumber not found for user');
                                                }
                                            }}>
                                                {displayUsername}
                                            </p>
                                        </div>
                                        <p className='col-2'>{Math.round(item.points)}</p>
                                        {shouldShowBoostPoints() && <p className='col-2'>{item.boostPoints && Math.round(item.boostPoints)}</p>}
                                        {shouldShowEventsParticipated() && <p className='col-2'>{item?.teamCount}</p>}
                                        {shouldShowEventsWon() && <p className='col-2'>{item.winCount ? item.winCount : 0}</p>}
                                        {shouldShowRewards() && <div className='col-3 reward-container'>
                                            <p className='reward-text' style={getRewardStyle(rewardText)}>{isInactive ? '-' : rewardText}</p>
                                        </div>}
                                    </div>
                                );
                            })}
                            {placeholders.map((_, i) => (
                                <div className="leaderboard-entry-container placeholder" key={`placeholder-${i}`} />
                            ))}
                        </>
                    ) : (
                        error ? (
                            <div className='no-packs-container'>
                                <p>No Entries Fetched</p>
                            </div>
                        ) : ((loading || isLoadingLeague) && (
                            <div className='no-packs-container'>
                                <div className='loader' />
                            </div>
                        )
                        )
                    )}
                </div>
                <div className='pagination'>
                    <button className='default-button'
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1 || totalPages === 0}
                    >
                        Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button className='default-button'
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                    >
                        Next
                    </button>
                </div>
            </div>

            {(event || eventDropdown) && showOverlay && (
                <div className="overlay" onClick={closeOverlay}>
                    <FantasyTeamView userID={selectedUserID} event={event ? event : eventDropdown ? eventDropdown : null} onClose={closeOverlay} />
                </div>
            )}

            {(season && !event && !eventDropdown) && showOverlay && (
                <div className={`overlay ${selectedEvent && 'no-background'}`} onClick={closeOverlay}>
                    <div className='card-menu-container' style={{ height: "100%" }}>
                        <h1 className='title-eventpage'>
                            <span
                                className='title-username-clickable'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const user = leaderboard.find(item => item._id === selectedUserID);
                                    if (user && user.userNumber) {
                                        navigate(`/profile/${user.userNumber}`);
                                    } else {
                                        console.error('userNumber not found for user');
                                    }
                                }}
                            >
                                {getUsername(selectedUserID)}
                            </span>
                            <span style={{ fontFamily: "fantasy" }}>'</span>s Fantasy Teams for {season.name}
                        </h1>
                        <button className="overlay-close" onClick={closeOverlay}>X</button>
                        <div className='overlay-content' onClick={(e) => e.stopPropagation()}>
                            <div className='event-desc-container'>
                                <p className='col-7'> Event </p>
                                <p className='col-3'> Points </p>
                                <p className='col-2'> Placement </p>
                            </div>

                            <div className='leaderboard-scroll-container'>
                                {leaderboard && leaderboard.map((item, index) => (
                                    // Display all the leaderboard entries with the same id as the selected user
                                    item._id === selectedUserID && (
                                        item.teams && item.teams.map((team, index) => (
                                            <div key={index} className="event-entry-container" onClick={() => setSelectedEvent(team.event)} >
                                                <div className='col-7' style={{ height: "100%", display: "flex", justifyContent: "center" }}>
                                                    <p className='username-clickable' onClick={(e) => { e.stopPropagation(); navigate(`/event/${team.event.hltv_id}`); }}> {team.event.name} </p>
                                                </div>
                                                <p className='col-3' style={getPointsStyle(team.points)}> {team.points.toFixed(2)} </p>
                                                <p className='col-2'> {team.placement ? team.placement : "?"} </p>
                                            </div>
                                        ))
                                    )
                                ))}
                            </div>

                        </div>
                    </div>
                </div>
            )}
            {selectedEvent && (
                <FantasyTeamView userID={selectedUserID} event={selectedEvent} onClose={closeSelectedEvent} overlay={true} />
            )}
        </div>
    );
};

export default Leaderboard;